import React, { useState, useEffect } from "react";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import MiniDrawer from "../../components/MiniDrawer/MiniDrawer";
import Wrapper from "../../components/UI/Wrapper";
import SearchIcon from "@mui/icons-material/Search";
import "./RateSheet.css";
import axios from "axios";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

function RateSheet() {
  const baseAPI = process.env.REACT_APP_API_URL;
  const itemsPerPage = 100;
  const [currentPage, setCurrentPage] = useState(1);
  const [rateRequestData, setRateRequestData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedDetail, setSelectedDetail] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const super_admin = localStorage.getItem("super_admin");

  // Vendor list from API
  const [vendorList, setVendorList] = useState([]);

  // ─── Filter state ────────────────────────────────────────────────────────────
  const [filters, setFilters] = useState({
    leadId: "",
    originAirport: "",
    destinationAirport: "",
    airlineName: "",
    leadOwner: "",
    chargeStatus: "requested",
  });

  // Freight form state
  const [freightForm, setFreightForm] = useState({
    chargeableWeight: "",
    minimumChargeableWeight: "",
    additionalChargeableWeight: "",
    dueCarrier: "",
    vendor_id: "",
    buying: "",
    selling: "",
    freightRemarks: "",
  });

  // Overseas form state
  const [overseasForm, setOverseasForm] = useState({
    importPermit: "",
    importCustomClearance: "",
    doorToDoor: "",
    quarantineCharges: "",
    cdcDogImportClearance: "",
    overseasRemarks: "",
    exportRates: "",
  });

  useEffect(() => {
    fetchRateRequestDetails();
    fetchVendors();
  }, []);

  useEffect(() => {
    const cw = parseFloat(freightForm.chargeableWeight) || 0;
    const minCw = parseFloat(freightForm.minimumChargeableWeight) || 0;
    const addCw = minCw > cw ? minCw - cw : 0;
    const buying = parseFloat(freightForm.buying);
    const dueCarrier = parseFloat(freightForm.dueCarrier) || 0;

    if (!buying) return;

    const baseWeight = cw < minCw ? minCw : cw;
    const totalWeight = baseWeight;

    if (!totalWeight) return;

    // Buying amount (weight-based only, excluding due carrier)
    const buyingWeightAmount = totalWeight * buying;

    // Full buying amount includes due carrier
    const buyingTotalAmount = buyingWeightAmount + dueCarrier;

    // Selling = buying total + margin
    const sellingTotalAmount = buyingTotalAmount + MARGIN;

    // Selling rate = (sellingTotal - dueCarrier) / totalWeight  ← carrier excluded from rate
    const sellingRate = Math.round(
      (sellingTotalAmount - dueCarrier) / totalWeight,
    );

    setFreightForm((prev) => ({ ...prev, selling: String(sellingRate) }));
  }, [
    freightForm.buying,
    freightForm.chargeableWeight,
    freightForm.minimumChargeableWeight,
    freightForm.dueCarrier,
  ]);

  const fetchRateRequestDetails = () => {
    setLoading(true);
    const storedToken = JSON.parse(localStorage.getItem("token"));
    if (storedToken) {
      axios
        .get(`${baseAPI}userget/GetRateRequestDetail?rate_request_id=`, {
          headers: { Authorization: `Bearer ${storedToken}` },
        })
        .then((response) => {
          const result = response.data?.data;
          setRateRequestData(Array.isArray(result) ? result : []);
        })
        .catch((error) => {
          console.error("Error fetching rate request details:", error);
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  };

  // Fetch vendors from API
  const fetchVendors = () => {
    const storedToken = JSON.parse(localStorage.getItem("token"));
    if (storedToken) {
      axios
        .get(`${baseAPI}adminget/GETSupplier?SupplierID=1&opt=2`, {
          headers: { Authorization: `Bearer ${storedToken}` },
        })
        .then((response) => {
          const result = response.data?.data ?? response.data;
          setVendorList(Array.isArray(result) ? result : []);
        })
        .catch((error) => {
          console.error("Error fetching vendors:", error);
        });
    }
  };

  // ─── Dropdown options derived from API data (alphabetically sorted, unique) ──

  const getUniqueOptions = (key) => {
    const safeData = Array.isArray(rateRequestData) ? rateRequestData : [];
    const unique = [
      ...new Set(safeData.map((item) => item[key]).filter(Boolean)),
    ];
    return unique.sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: "base" }),
    );
  };

  const originAirportOptions = getUniqueOptions("origin_airport");
  const destinationAirportOptions = getUniqueOptions("destination_airport");
  const airlineNameOptions = getUniqueOptions("airlines_name");
  const leadOwnerOptions = getUniqueOptions("lead_owner");

  // ─── Filtered data ────────────────────────────────────────────────────────────

  const safeData = Array.isArray(rateRequestData) ? rateRequestData : [];

  const filteredData = safeData.filter((item) => {
    const matchLeadId = filters.leadId
      ? String(item.lead_id)
          .toLowerCase()
          .includes(filters.leadId.toLowerCase())
      : true;
    const matchOrigin = filters.originAirport
      ? item.origin_airport === filters.originAirport
      : true;
    const matchDestination = filters.destinationAirport
      ? item.destination_airport === filters.destinationAirport
      : true;
    const matchAirline = filters.airlineName
      ? item.airlines_name === filters.airlineName
      : true;
    const matchLeadOwner = filters.leadOwner
      ? item.lead_owner === filters.leadOwner
      : true;

    const matchChargeStatus =
      filters.chargeStatus === "requested"
        ? item.freight_charge_status?.toLowerCase() === "requested" ||
          item.overseas_charge_status?.toLowerCase() === "requested"
        : true;

    return (
      matchLeadId &&
      matchOrigin &&
      matchDestination &&
      matchAirline &&
      matchLeadOwner &&
      matchChargeStatus
    );
  });

  //const hasActiveFilter = Object.values(filters).some((v) => v !== "");
  const hasActiveFilter =
    filters.leadId !== "" ||
    filters.originAirport !== "" ||
    filters.destinationAirport !== "" ||
    filters.airlineName !== "" ||
    filters.leadOwner !== "" ||
    filters.chargeStatus !== "requested";

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setCurrentPage(1); // reset to first page on filter change
  };

  const handleClearFilters = () => {
    setFilters({
      leadId: "",
      originAirport: "",
      destinationAirport: "",
      airlineName: "",
      leadOwner: "",
      chargeStatus: "requested",
    });
    setCurrentPage(1);
  };

  // ─── Computed amounts ─────────────────────────────────────────────────────────

  const derivedAdditionalCW = (() => {
    const cw = parseFloat(freightForm.chargeableWeight) || 0;
    const min = parseFloat(freightForm.minimumChargeableWeight) || 0;
    return min > cw ? min - cw : 0;
  })();

  const computeFreightAmount = (rateValue) => {
    const cw = parseFloat(freightForm.chargeableWeight) || 0;
    const minCw = parseFloat(freightForm.minimumChargeableWeight) || 0;
    const addCw = derivedAdditionalCW; // ── CHANGE 1
    const dueCarrier = parseFloat(freightForm.dueCarrier) || 0;
    const rate = parseFloat(rateValue) || 0;

    if (!rate) return null;

    const baseWeight = cw < minCw ? minCw : cw;
    const amount = baseWeight * rate + dueCarrier;
    return amount;
  };

  const computedFreightBuyingAmount = computeFreightAmount(freightForm.buying);
  const computedFreightSellingAmount = computeFreightAmount(
    freightForm.selling,
  );

  const MARGIN = 30000;

  const computedOverseasAmount = (() => {
    const permit = parseFloat(overseasForm.importPermit) || 0;
    const customs = parseFloat(overseasForm.importCustomClearance) || 0;
    const door = parseFloat(overseasForm.doorToDoor) || 0;
    const quarantine = parseFloat(overseasForm.quarantineCharges) || 0;
    const cdc = parseFloat(overseasForm.cdcDogImportClearance) || 0;
    const exportRates = parseFloat(overseasForm.exportRates) || 0;
    const total = permit + customs + door + quarantine + cdc + exportRates;
    return total > 0 ? total : null;
  })();

  // ─── Pagination ───────────────────────────────────────────────────────────────

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const visibleData = filteredData.slice(startIndex, endIndex);
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > totalPages) return;
    setCurrentPage(newPage);
  };

  // ─── Modal ────────────────────────────────────────────────────────────────────

  const handleDetailClick = (item) => {
    setSelectedDetail(item);
    setShowModal(true);
    setFreightForm({
      chargeableWeight: item.freight_chargeable_weight || "",
      minimumChargeableWeight: item.freight_minimum_chargeable_weight || "",
      additionalChargeableWeight:
        item.freight_additional_chargeable_weight || "",
      dueCarrier: item.freight_due_carrier || "",
      vendor_id: item.vendor_id || "",
      buying: item.freight_buying || "",
      selling: item.frieght_selling || "",
      freightRemarks: item.freight_remarks || "",
    });
    setOverseasForm({
      importPermit: item.overseas_import_permit || "",
      importCustomClearance: item.overseas_import_custom_clearance || "",
      doorToDoor: item.overseas_door_to_door || "",
      quarantineCharges: item.quarantine_charges || "",
      cdcDogImportClearance: item.cdc_dog_import_clearance || "",
      exportRates: item.export_rates || "",
      overseasRemarks: item.overseas_remarks || "",
    });
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedDetail(null);
  };

  // ─── Submit ───────────────────────────────────────────────────────────────────

  const handleAdd = (sendmail = 1) => {
    if (!selectedDetail) return;
    const storedToken = JSON.parse(localStorage.getItem("token"));
    if (!storedToken) return;

    setSubmitting(true);

    const payload = {
      rate_request_id: selectedDetail.rate_request_id,
      freight_chargeable_weight: freightForm.chargeableWeight || null,
      freight_minimum_chargeable_weight:
        freightForm.minimumChargeableWeight || null,
      freight_additional_chargeable_weight:
        freightForm.additionalChargeableWeight || null,
      freight_due_carrier: freightForm.dueCarrier || null,
      vendor_id: freightForm.vendor_id || null,
      freight_buying: freightForm.buying || null,
      frieght_selling: freightForm.selling || null,
      freight_charge_amount: computedFreightSellingAmount ?? null,
      freight_remarks: freightForm.freightRemarks || null,
      overseas_import_permit: overseasForm.importPermit || null,
      overseas_import_custom_clearance:
        overseasForm.importCustomClearance || null,
      overseas_door_to_door: overseasForm.doorToDoor || null,
      quarantine_charges: overseasForm.quarantineCharges || null,
      cdc_dog_import_clearance: overseasForm.cdcDogImportClearance || null,
      overseas_charge_amount: computedOverseasAmount ?? null,
      overseas_remarks: overseasForm.overseasRemarks || null,
      request_status: selectedDetail.request_status,
      response_notes: selectedDetail.response_notes || null,
      export_rates: overseasForm.exportRates || null,
      sendmail: sendmail || null,
    };

    axios
      .post(`${baseAPI}userpost/UpdateRateRequest`, payload, {
        headers: { Authorization: `Bearer ${storedToken}` },
      })
      .then(() => {
        fetchRateRequestDetails();
        handleCloseModal();
      })
      .catch((error) => {
        console.error("Error updating rate request:", error);
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const handleDelete = (rateRequestId) => {
    if (!window.confirm("Are you sure you want to delete this rate request?"))
      return;

    const storedToken = JSON.parse(localStorage.getItem("token"));
    if (!storedToken) return;

    setDeletingId(rateRequestId);

    axios
      .post(
        `${baseAPI}userpost/DeleteRateRequest`,
        { rate_request_id: rateRequestId },
        { headers: { Authorization: `Bearer ${storedToken}` } },
      )
      .then(() => {
        fetchRateRequestDetails();
      })
      .catch((error) => {
        console.error("Error deleting rate request:", error);
      })
      .finally(() => {
        setDeletingId(null);
      });
  };

  // ─── Helpers ──────────────────────────────────────────────────────────────────

  const renderChargeStatus = (status) => {
    if (status === "-") return <span>-</span>;
    if (status?.toLowerCase() === "requested")
      return (
        <span style={{ color: "red", fontWeight: "bold" }}>REQUESTED</span>
      );
    return <span style={{ color: "green", fontWeight: "bold" }}>{status}</span>;
  };

  const freightEnabled =
    selectedDetail?.freight_charges === 1 ||
    selectedDetail?.freight_charges === true;
  const overseasEnabled =
    selectedDetail?.overseas_charges === 1 ||
    selectedDetail?.overseas_charges === true;

  const formatDate = (dateString) => {
    const options = {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    };
    return new Date(dateString).toLocaleString("en-US", options);
  };

  const handleExportExcel = () => {
    const exportData = filteredData.map((item, index) => ({
      "Sr No": index + 1,
      "Lead ID": item.lead_id,
      "Date of Request": formatDate(item.created_at),
      "Origin Airport": item.origin_airport,
      "Destination Airport": item.destination_airport,
      "Airline Name": item.airlines_name,
      "Lead Owner": item.lead_owner,
      "Vendor Name": item.vendor_name || "-",
      "Pet Crate": item.petCrateName || "-",
      "Freight Charge Remarks": item.freight_remarks || "-",
      "Overseas Charge Remarks": item.overseas_remarks || "-",
      "Freight Charges": item.freight_charge_status || "-",
      "Overseas Charges": item.overseas_charge_status || "-",
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Rate Sheet");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const data = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
    });

    saveAs(data, `RateSheet_${new Date().getTime()}.xlsx`);
  };

  // ─── Render ───────────────────────────────────────────────────────────────────

  return (
    <>
      <div className="d-flex flex-row app">
        <MiniDrawer />
        <div
          className="container-fluid"
          style={{ padding: "4rem 1rem 1rem 1rem", backgroundColor: "#F4F4F4" }}
        >
          <div
            className="d-flex flex-column w-100"
            style={{ backgroundColor: "#F4F4F4" }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
              }}
            >
              <div>
                <h1 style={{ fontSize: "24px", marginBottom: "2rem" }}>
                  Pricing | <HomeOutlinedIcon />{" "}
                  <span style={{ fontSize: "20px" }}>&gt; Rate Sheet</span>
                </h1>
              </div>
            </div>

            <div>
              <Wrapper className="ViewLeadDetails">
                <div className="container-fluid">
                  {/* ── Filters ── */}
                  <div
                    className="rs-filters-bar"
                    style={{
                      display: "flex",
                      alignItems: "flex-end",
                      gap: "12px",
                      flexWrap: "wrap",
                    }}
                  >
                    {/* Lead ID — text input */}
                    <div className="rs-filter-item">
                      <label className="rs-filter-label">Lead ID</label>
                      <input
                        type="text"
                        className="rs-filter-input"
                        placeholder="Search Lead ID..."
                        value={filters.leadId}
                        onChange={(e) =>
                          handleFilterChange("leadId", e.target.value)
                        }
                      />
                    </div>

                    {/* Origin Airport — dropdown */}
                    <div className="rs-filter-item">
                      <label className="rs-filter-label">Origin Airport</label>
                      <select
                        className="rs-filter-select"
                        value={filters.originAirport}
                        onChange={(e) =>
                          handleFilterChange("originAirport", e.target.value)
                        }
                      >
                        <option value="">All</option>
                        {originAirportOptions.map((opt) => (
                          <option key={opt} value={opt}>
                            {opt}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Destination Airport — dropdown */}
                    <div className="rs-filter-item">
                      <label className="rs-filter-label">
                        Destination Airport
                      </label>
                      <select
                        className="rs-filter-select"
                        value={filters.destinationAirport}
                        onChange={(e) =>
                          handleFilterChange(
                            "destinationAirport",
                            e.target.value,
                          )
                        }
                      >
                        <option value="">All</option>
                        {destinationAirportOptions.map((opt) => (
                          <option key={opt} value={opt}>
                            {opt}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Airline Name — dropdown */}
                    <div className="rs-filter-item">
                      <label className="rs-filter-label">Airline Name</label>
                      <select
                        className="rs-filter-select"
                        value={filters.airlineName}
                        onChange={(e) =>
                          handleFilterChange("airlineName", e.target.value)
                        }
                      >
                        <option value="">All</option>
                        {airlineNameOptions.map((opt) => (
                          <option key={opt} value={opt}>
                            {opt}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Lead Owner — dropdown */}
                    <div className="rs-filter-item">
                      <label className="rs-filter-label">Lead Owner</label>
                      <select
                        className="rs-filter-select"
                        value={filters.leadOwner}
                        onChange={(e) =>
                          handleFilterChange("leadOwner", e.target.value)
                        }
                      >
                        <option value="">All</option>
                        {leadOwnerOptions.map((opt) => (
                          <option key={opt} value={opt}>
                            {opt}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="rs-filter-item">
                      <label className="rs-filter-label">Charge Status</label>
                      <select
                        className="rs-filter-select"
                        value={filters.chargeStatus}
                        onChange={(e) =>
                          handleFilterChange("chargeStatus", e.target.value)
                        }
                      >
                        <option value="requested">Requested</option>
                        <option value="">Show All</option>
                      </select>
                    </div>

                    {/* Clear button — only shown when a filter is active */}
                    {hasActiveFilter && (
                      <div className="rs-filter-item rs-filter-clear-wrap">
                        <label className="rs-filter-label">&nbsp;</label>
                        <button
                          className="rs-filter-clear-btn"
                          onClick={handleClearFilters}
                        >
                          Clear Filters
                        </button>
                      </div>
                    )}

                    <div
                      style={{
                        marginLeft: "auto",
                        display: "flex",
                        alignItems: "flex-end",
                      }}
                    >
                      <button
                        onClick={handleExportExcel}
                        style={{
                          backgroundColor: "#1D6F42",
                          color: "#fff",
                          border: "none",
                          padding: "10px 18px",
                          borderRadius: "6px",
                          cursor: "pointer",
                          fontWeight: "600",
                          height: "40px",
                          whiteSpace: "nowrap",
                        }}
                      >
                        Export Excel
                      </button>
                    </div>
                  </div>

                  {/* Result count */}
                  {hasActiveFilter && (
                    <div className="rs-filter-count">
                      Showing <strong>{filteredData.length}</strong> of{" "}
                      <strong>{safeData.length}</strong> records
                    </div>
                  )}

                  <div className="rateSheet">
                    <table className="">
                      <thead>
                        <tr className="rateSheetHeading">
                          <th>#</th>
                          <th>Lead Id</th>
                          <th>Date of Request</th>
                          <th>Origin Airport</th>
                          <th>Destination Airport</th>
                          <th>Airline Name</th>
                          <th>Lead Owner</th>
                          {super_admin === "true" && <th>Vendor Name</th>}
                          <th>Pet Crate</th>
                          <th>Freight Charge Remarks</th>
                          <th>Overseas Charge Remarks</th>
                          <th>Freight Charges</th>
                          <th>Overseas Charges</th>
                          {super_admin === "true" && (
                            <>
                              <th>Show Details</th>
                              <th>Delete Request</th>
                            </>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {loading ? (
                          <tr>
                            <td colSpan="14" style={{ textAlign: "center" }}>
                              Loading...
                            </td>
                          </tr>
                        ) : visibleData?.length > 0 ? (
                          visibleData.map((item, index) => (
                            <tr key={item.rate_request_id}>
                              <td>
                                {String(startIndex + index + 1).padStart(
                                  2,
                                  "0",
                                )}
                              </td>
                              <td>{item.lead_id}</td>
                              <td>{formatDate(new Date(item.created_at))}</td>
                              <td>{item.origin_airport}</td>
                              <td>{item.destination_airport}</td>
                              <td>{item.airlines_name}</td>
                              <td>{item.lead_owner}</td>
                              {super_admin === "true" && (
                                <td>{item.vendor_name || "-"}</td>
                              )}
                              <td>{item.petCrateName || "-"}</td>
                              <td title={item.freight_remarks || ""}>
                                {item.freight_remarks
                                  ? item.freight_remarks.length > 30
                                    ? item.freight_remarks.slice(0, 30) + "..."
                                    : item.freight_remarks
                                  : "-"}
                              </td>
                              <td title={item.overseas_remarks || ""}>
                                {item.overseas_remarks
                                  ? item.overseas_remarks.length > 30
                                    ? item.overseas_remarks.slice(0, 30) + "..."
                                    : item.overseas_remarks
                                  : "-"}
                              </td>
                              <td>
                                {renderChargeStatus(item.freight_charge_status)}
                              </td>
                              <td>
                                {renderChargeStatus(
                                  item.overseas_charge_status,
                                )}
                              </td>
                              {super_admin === "true" && (
                                <>
                                  <td>
                                    <button
                                      onClick={() => handleDetailClick(item)}
                                      style={{ height: "30px", width: "38px" }}
                                      title="View Details"
                                    >
                                      <SearchIcon className="icon" />
                                    </button>
                                  </td>
                                  <td>
                                    <button
                                      onClick={() =>
                                        handleDelete(item.rate_request_id)
                                      }
                                      disabled={
                                        deletingId === item.rate_request_id
                                      }
                                      title="Delete"
                                      style={{
                                        height: "30px",
                                        width: "38px",
                                        background: "none",
                                        border: "1px solid #e0574f",
                                        borderRadius: "4px",
                                        cursor: "pointer",
                                        color: "#e0574f",
                                        fontSize: "16px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        opacity:
                                          deletingId === item.rate_request_id
                                            ? 0.5
                                            : 1,
                                      }}
                                    >
                                      {deletingId === item.rate_request_id
                                        ? "…"
                                        : "🗑"}
                                    </button>
                                  </td>
                                </>
                              )}
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="14">No data to display</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                    {/* Pagination */}
                    <nav
                      aria-label="Page navigation"
                      style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        marginTop: "1rem",
                      }}
                    >
                      <ul className="pagination">
                        <li
                          className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                        >
                          <a
                            className="page-link"
                            onClick={() => handlePageChange(currentPage - 1)}
                            style={{ cursor: "pointer" }}
                          >
                            Previous
                          </a>
                        </li>
                        {currentPage > 1 && (
                          <li className="page-item">
                            <a
                              className="page-link"
                              onClick={() => handlePageChange(currentPage - 1)}
                              style={{ cursor: "pointer" }}
                            >
                              {currentPage - 1}
                            </a>
                          </li>
                        )}
                        <li className="page-item active">
                          <a className="page-link">{currentPage}</a>
                        </li>
                        {currentPage < totalPages && (
                          <li className="page-item">
                            <a
                              className="page-link"
                              onClick={() => handlePageChange(currentPage + 1)}
                              style={{ cursor: "pointer" }}
                            >
                              {currentPage + 1}
                            </a>
                          </li>
                        )}
                        <li
                          className={`page-item ${endIndex >= filteredData.length ? "disabled" : ""}`}
                        >
                          <a
                            className="page-link"
                            onClick={() => handlePageChange(currentPage + 1)}
                            style={{ cursor: "pointer" }}
                          >
                            Next
                          </a>
                        </li>
                      </ul>
                    </nav>
                  </div>
                </div>
              </Wrapper>
            </div>
          </div>
        </div>
      </div>

      {/* ── Detail Modal ──────────────────────────────────────────────────────── */}
      {showModal && selectedDetail && (
        <div className="rs-overlay" onClick={handleCloseModal}>
          <div className="rs-modal" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="rs-modal-header">
              <h2 className="rs-modal-title">
                Lead ID : {selectedDetail.lead_id}
              </h2>
              <span>
                <button
                  className="rs-close-btn rs-modal-header-button"
                  onClick={handleCloseModal}
                >
                  ✕
                </button>
              </span>
            </div>

            {/* Lead meta info */}
            <div className="rs-info-grid">
              <div className="rs-info-row">
                <span className="rs-info-label">Customer Name</span>
                <span className="rs-info-colon">:</span>
                <span className="rs-info-value">
                  {selectedDetail.customer_name || "-"}
                </span>
              </div>
              <div className="rs-info-row">
                <span className="rs-info-label">Pet Type &amp; Breed</span>
                <span className="rs-info-colon">:</span>
                <span className="rs-info-value">
                  {selectedDetail.PetType || "-"}
                  {selectedDetail.PetBreedType
                    ? ` | ${selectedDetail.PetBreedType}`
                    : ""}
                </span>
              </div>
              <div className="rs-info-row">
                <span className="rs-info-label">Pet's Dimension (A,B,C,D)</span>
                <span className="rs-info-colon">:</span>
                <span className="rs-info-value">
                  {selectedDetail.pet_dimensions || "-"}
                </span>
              </div>
              <div className="rs-info-row">
                <span className="rs-info-label">Origin Airport</span>
                <span className="rs-info-colon">:</span>
                <span className="rs-info-value">
                  {selectedDetail.origin_airport}
                </span>
              </div>
              <div className="rs-info-row">
                <span className="rs-info-label">Destination Airport</span>
                <span className="rs-info-colon">:</span>
                <span className="rs-info-value">
                  {selectedDetail.destination_airport}
                </span>
              </div>
              <div className="rs-info-row">
                <span className="rs-info-label">Airline Name</span>
                <span className="rs-info-colon">:</span>
                <span className="rs-info-value">
                  {selectedDetail.airlines_name}
                </span>
              </div>
              <div className="rs-info-row">
                <span className="rs-info-label">Crate</span>
                <span className="rs-info-colon">:</span>
                <span className="rs-info-value">
                  {selectedDetail.petCrateName || "-"}
                </span>
              </div>
            </div>

            {/* ── Freight Charges ── */}
            <div
              className={`rs-section ${!freightEnabled ? "rs-section-disabled" : ""}`}
            >
              <div className="rs-section-heading">
                <h3 className="rs-section-title">
                  Freight <span className="rs-highlight">Charges</span>
                </h3>
                <div className="rs-section-bar" />
              </div>

              <div className="rs-form-grid">
                <div className="rs-field rs-field-width">
                  <label>Actual Chargeable Weight</label>
                  <input
                    type="number"
                    placeholder="–"
                    disabled={!freightEnabled}
                    value={freightForm.chargeableWeight}
                    onChange={(e) =>
                      setFreightForm({
                        ...freightForm,
                        chargeableWeight: e.target.value,
                      })
                    }
                    onWheel={(e) => e.target.blur()}
                  />
                </div>
                <div className="rs-field rs-field-width">
                  <label>Minimum Chargeable Weight</label>
                  <input
                    type="number"
                    placeholder="–"
                    disabled={!freightEnabled}
                    value={freightForm.minimumChargeableWeight}
                    onChange={(e) =>
                      setFreightForm({
                        ...freightForm,
                        minimumChargeableWeight: e.target.value,
                      })
                    }
                    onWheel={(e) => e.target.blur()}
                  />
                </div>
                <div className="rs-field rs-field-width">
                  <label>Additional Chargeable Weight</label>
                  <input
                    type="number"
                    placeholder="–"
                    disabled={true}
                    value={derivedAdditionalCW || ""}
                    className="rs-field-readonly"
                  />
                </div>
                <div className="rs-field rs-field-width">
                  <label>Due Carrier</label>
                  <input
                    type="number"
                    placeholder="–"
                    disabled={!freightEnabled}
                    value={freightForm.dueCarrier}
                    onChange={(e) =>
                      setFreightForm({
                        ...freightForm,
                        dueCarrier: e.target.value,
                      })
                    }
                  />
                </div>

                {/* Vendor bound from API */}
                <div className="rs-field rs-field-width">
                  <label>Choose Vendor</label>
                  <select
                    disabled={!freightEnabled}
                    value={freightForm.vendor_id}
                    onChange={(e) =>
                      setFreightForm({
                        ...freightForm,
                        vendor_id: e.target.value,
                      })
                    }
                  >
                    <option value="">Choose</option>
                    {vendorList.map((v) => (
                      <option key={v.Vendor_Id} value={v.Vendor_Id}>
                        {v.VendorFirstName} {v.VendorLastName}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Buying — for reference only */}
                <div className="rs-field rs-field-width">
                  <label>
                    Buying <span className="rs-ref-note">(reference)</span>
                  </label>
                  <input
                    type="number"
                    placeholder="–"
                    disabled={!freightEnabled}
                    value={freightForm.buying}
                    onWheel={(e) => e.target.blur()}
                    onChange={(e) =>
                      setFreightForm({ ...freightForm, buying: e.target.value })
                    }
                  />
                </div>

                {/* Selling field */}
                <div className="rs-field">
                  <label>Selling</label>
                  <input
                    type="number"
                    placeholder="–"
                    disabled={!freightEnabled}
                    value={freightForm.selling}
                    onWheel={(e) => e.target.blur()}
                    onChange={(e) =>
                      setFreightForm({
                        ...freightForm,
                        selling: e.target.value,
                      })
                    }
                  />
                </div>

                {/* Freight Remarks — full width */}
                <div className="rs-field rs-field-full">
                  <label>Freight Remarks</label>
                  <textarea
                    placeholder="Enter remarks..."
                    disabled={!freightEnabled}
                    value={freightForm.freightRemarks}
                    onChange={(e) =>
                      setFreightForm({
                        ...freightForm,
                        freightRemarks: e.target.value,
                      })
                    }
                    rows={2}
                  />
                </div>
              </div>

              {/* Show both buying (ref) and selling computed amounts */}
              {freightEnabled &&
                (computedFreightBuyingAmount !== null ||
                  computedFreightSellingAmount !== null) && (
                  <div className="rs-computed-row">
                    {computedFreightBuyingAmount !== null && (
                      <div className="rs-computed-label">
                        Freight Buying Amount:{" "}
                        <strong>
                          ₹ {computedFreightBuyingAmount.toFixed(2)}
                        </strong>
                      </div>
                    )}
                    {computedFreightSellingAmount !== null && (
                      <div className="rs-computed-label rs-computed-selling">
                        Freight Selling Amount:{" "}
                        <strong>
                          ₹ {computedFreightSellingAmount.toFixed(2)}
                        </strong>
                      </div>
                    )}
                  </div>
                )}
            </div>

            {/* ── Overseas Charges ── */}
            <div
              className={`rs-section ${!overseasEnabled ? "rs-section-disabled" : ""}`}
            >
              <div className="rs-section-heading">
                <h3 className="rs-section-title">
                  Overseas{" "}
                  <span className="rs-highlight">
                    Charges{" "}
                    {overseasEnabled &&
                      selectedDetail?.overseas_requested_for && (
                        <span
                          style={{
                            fontWeight: 400,
                            fontSize: "13px",
                            color: "#666",
                          }}
                        >
                          (Requested for{" "}
                          <b style={{ color: "#4B3F9E" }}>
                            {selectedDetail.overseas_requested_for}
                          </b>
                          )
                        </span>
                      )}
                  </span>
                </h3>
                <div className="rs-section-bar" />
              </div>

              <div className="rs-form-grid">
                <div className="rs-field rs-field-width">
                  <label>Import Permit</label>
                  <input
                    type="number"
                    placeholder="–"
                    disabled={!overseasEnabled}
                    value={overseasForm.importPermit}
                    onWheel={(e) => e.target.blur()}
                    onChange={(e) =>
                      setOverseasForm({
                        ...overseasForm,
                        importPermit: e.target.value,
                      })
                    }
                  />
                </div>
                <div className="rs-field rs-field-width">
                  <label>Import Custom Clearance</label>
                  <input
                    type="number"
                    placeholder="–"
                    disabled={!overseasEnabled}
                    value={overseasForm.importCustomClearance}
                    onWheel={(e) => e.target.blur()}
                    onChange={(e) =>
                      setOverseasForm({
                        ...overseasForm,
                        importCustomClearance: e.target.value,
                      })
                    }
                  />
                </div>
                <div className="rs-field rs-field-width">
                  <label>Door-to-Door</label>
                  <input
                    type="number"
                    placeholder="–"
                    disabled={!overseasEnabled}
                    value={overseasForm.doorToDoor}
                    onWheel={(e) => e.target.blur()}
                    onChange={(e) =>
                      setOverseasForm({
                        ...overseasForm,
                        doorToDoor: e.target.value,
                      })
                    }
                  />
                </div>

                {/* Quarantine Charges */}
                <div className="rs-field rs-field-width">
                  <label>Quarantine Charges</label>
                  <input
                    type="number"
                    placeholder="–"
                    disabled={!overseasEnabled}
                    value={overseasForm.quarantineCharges}
                    onWheel={(e) => e.target.blur()}
                    onChange={(e) =>
                      setOverseasForm({
                        ...overseasForm,
                        quarantineCharges: e.target.value,
                      })
                    }
                  />
                </div>

                {/* CDC Dog Import Clearance */}
                <div className="rs-field rs-field-width">
                  <label>CDC Dog Import Clearance</label>
                  <input
                    type="number"
                    placeholder="–"
                    disabled={!overseasEnabled}
                    value={overseasForm.cdcDogImportClearance}
                    onWheel={(e) => e.target.blur()}
                    onChange={(e) =>
                      setOverseasForm({
                        ...overseasForm,
                        cdcDogImportClearance: e.target.value,
                      })
                    }
                  />
                </div>
                {/* Export Rates */}
                <div className="rs-field rs-field-width">
                  <label>Export Rates</label>
                  <input
                    type="number"
                    placeholder="–"
                    disabled={!overseasEnabled}
                    value={overseasForm.exportRates}
                    onWheel={(e) => e.target.blur()}
                    onChange={(e) =>
                      setOverseasForm({
                        ...overseasForm,
                        exportRates: e.target.value,
                      })
                    }
                  />
                </div>

                {/* Overseas Remarks — full width */}
                <div className="rs-field rs-field-full">
                  <label>Overseas Remarks</label>
                  <textarea
                    placeholder="Enter remarks..."
                    disabled={!overseasEnabled}
                    value={overseasForm.overseasRemarks}
                    onChange={(e) =>
                      setOverseasForm({
                        ...overseasForm,
                        overseasRemarks: e.target.value,
                      })
                    }
                    rows={2}
                  />
                </div>
              </div>

              {/* Overseas computed amount */}
              {overseasEnabled && computedOverseasAmount !== null && (
                <div className="rs-computed-label">
                  Overseas Charges Amount:{" "}
                  <strong>₹ {computedOverseasAmount.toFixed(2)}</strong>
                </div>
              )}
            </div>

            {/* ── Single ADD button ── */}

            <div className="rs-footer">
              <button
                className="rs-cancel-btn"
                onClick={() => handleAdd(0)}
                disabled={submitting}
                style={{
                  marginRight: "10px",
                  backgroundColor: "#dc3545",
                  color: "#fff",
                }}
              >
                {submitting ? "Saving..." : "Cancel Request"}
              </button>
              <button
                className="rs-add-btn"
                onClick={() => handleAdd(1)}
                disabled={submitting || (!freightEnabled && !overseasEnabled)}
              >
                {submitting ? "Saving..." : "ADD"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal & Filter CSS ────────────────────────────────────────────────── */}
    </>
  );
}

export default RateSheet;
